import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetQuestionnaireSectionsQuery } from "../api/questionnaire";
import {
  QuestionnaireSectionStatus,
  QuestionnaireSectionsForNavigation,
} from "../../../api/src/types";

interface UseCurrentUserQuestionnaireState {
  isCurrentUserQuestionnaireComplete: boolean;
  firstIncompleteSection: QuestionnaireSectionsForNavigation | null;
  isLoading: boolean;
  error: unknown;
}

export const useCurrentUserQuestionnaireState = (
  userId: string | undefined,
  skip?: boolean,
): UseCurrentUserQuestionnaireState => {
  const {
    data: questionnaireSections,
    isLoading,
    error,
  } = useGetQuestionnaireSectionsQuery(
    userId && !skip ? { id: userId, includeStatus: true } : skipToken,
  );

  // Check if all questionnaire sections are complete or not applicable
  const isCurrentUserQuestionnaireComplete = useMemo(() => {
    if (!questionnaireSections) return false;
    return questionnaireSections.every(
      (section) =>
        section.status === QuestionnaireSectionStatus.COMPLETED ||
        section.status === QuestionnaireSectionStatus.NOT_APPLICABLE,
    );
  }, [questionnaireSections]);

  // Find the first incomplete section (NOT_STARTED or IN_PROGRESS)
  const firstIncompleteSection = useMemo(() => {
    if (!questionnaireSections) return null;
    return (
      questionnaireSections.find(
        (section) =>
          section.status === QuestionnaireSectionStatus.NOT_STARTED ||
          section.status === QuestionnaireSectionStatus.IN_PROGRESS,
      ) ?? null
    );
  }, [questionnaireSections]);

  return {
    isCurrentUserQuestionnaireComplete,
    firstIncompleteSection,
    isLoading,
    error,
  };
};
